import { useState, useEffect } from 'react';
import { Users, Activity, Flame, Loader2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { usePreferences } from '../context/PreferencesContext';

interface CohortActivity {
  type: string;
  message: string;
  timestamp: string;
}

interface CohortData {
  track: { slug: string; title: string };
  activeCount: number;
  activities: CohortActivity[];
}

interface CohortWidgetProps {
  trackSlug?: string;
}

export default function CohortWidget({ trackSlug }: CohortWidgetProps) {
  const { preferences } = usePreferences();
  const { fetchApi } = useApi();
  const [data, setData] = useState<CohortData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Only show for silent_cohort mode
  if (preferences.connection_mode !== 'silent_cohort') {
    return null;
  }

  useEffect(() => {
    if (!trackSlug) {
      setLoading(false);
      return;
    }

    loadCohortActivity();
  }, [trackSlug]);

  async function loadCohortActivity() {
    try {
      const response = await fetchApi<CohortData>(`/api/cohort/activity?track=${trackSlug}`);
      setData(response);
    } catch (err) {
      console.error('Failed to load cohort activity:', err);
      setError('Unable to load cohort activity');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-lg p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (error || !data || !trackSlug) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 border border-purple-500/20 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-5 h-5 text-purple-400" />
        <h3 className="text-lg font-semibold text-white">Your Cohort</h3>
      </div>

      <div className="mb-4">
        <p className="text-sm text-gray-400">{data.track.title}</p>
        <p className="text-2xl font-bold text-white">
          {data.activeCount} <span className="text-sm font-normal text-gray-400">active today</span>
        </p>
      </div>

      {data.activities.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-300">Recent activity:</p>
          {data.activities.slice(0, 5).map((activity, index) => (
            <div key={index} className="flex items-start gap-2 text-sm">
              {activity.type === 'streak' ? (
                <Flame className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
              ) : (
                <Activity className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
              )}
              <p className="text-gray-300">{activity.message}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400">
          No recent activity yet. Be the first to complete a lesson today!
        </p>
      )}
    </div>
  );
}
